'use client'

import { useEffect } from 'react'
import { useTranslations } from 'next-intl'
import { useMounted } from '@/components/util/client-date'
import { createPortal } from 'react-dom'
import { X } from 'lucide-react'
import { NotationGuideSteps } from '@/components/floggers/notation-guide'

/**
 * Centered, portaled modal wrapping the shared 4-step NotationGuideSteps
 * (knot chips, formula legend, build-plan walkthrough, consumption tables).
 * Portaled to body so it clears the sticky header / bench stacking contexts,
 * same as the lightbox. Wide enough for the rope-consumption tables.
 */
export function NotationGuideModal({
  open,
  onClose,
  slugs,
}: {
  open: boolean
  onClose: () => void
  slugs: string[]
}) {
  const t = useTranslations('floggerBench')
  const mounted = useMounted()

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      window.removeEventListener('keydown', onKey)
      document.body.style.overflow = prev
    }
  }, [open, onClose])

  if (!open || !mounted) return null

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t('notationGuideAria')}
      className="fixed inset-0 z-[80] flex items-start justify-center overflow-y-auto bg-black/60 p-4 pt-[7vh]"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-3xl bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label={t('closeNotationGuide')}
          className="absolute right-3 top-3 z-10 bg-black/5 p-2 text-gray-700 transition-colors hover:bg-black/10"
        >
          <X className="h-5 w-5" />
        </button>
        <div className="max-h-[84vh] overflow-y-auto p-6 pt-14 sm:p-8 sm:pt-14">
          <h2 className="mb-5 text-2xl font-bold text-gray-900">{t('notationGuide')}</h2>
          <NotationGuideSteps publishedSlugs={slugs} />
        </div>
      </div>
    </div>,
    document.body,
  )
}
